import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { SITE, whatsappWithText } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppButton";
import { SERVICES } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you within a few hours.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-[#1E2A78] placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E2A78] focus-visible:ring-offset-2 transition-shadow duration-150";

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us | MBA Project & Thesis Writing Help"
        description="Reach us on WhatsApp at +91 90040 31722 or email ceo@mbaprojectreport.com for a free academic writing quote. We reply within hours."
      />
      <PageHero
        eyebrow="Contact us"
        lines={["Tell us what", "you're working on."]}
        sub="Reach us on WhatsApp for the fastest response, or use the form below — we typically reply within a few hours during business hours."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Direct channels</Eyebrow>
            <div className="space-y-4">
              <a
                href={whatsappWithText("Hi, I'd like to discuss my requirement.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-card"
                className="flex items-center gap-4 border border-slate-200 rounded-2xl p-6 bg-white transition-[box-shadow,border-color] duration-300 hover:shadow-lg hover:border-slate-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shrink-0">
                  <WhatsAppIcon className="h-6 w-6" />
                </span>
                <div>
                  <p className="font-display font-bold text-base tracking-tight">WhatsApp — fastest</p>
                  <p className="text-sm text-slate-500">{SITE.phoneDisplay}</p>
                </div>
              </a>
              <a
                href={SITE.phoneHref}
                data-testid="contact-phone-card"
                className="flex items-center gap-4 border border-slate-200 rounded-2xl p-6 bg-white transition-[box-shadow,border-color] duration-300 hover:shadow-lg hover:border-slate-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1E2A78] text-white shrink-0">
                  <Phone className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-display font-bold text-base tracking-tight">Call us</p>
                  <p className="text-sm text-slate-500">{SITE.phoneDisplay}</p>
                </div>
              </a>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-card"
                className="flex items-center gap-4 border border-slate-200 rounded-2xl p-6 bg-white transition-[box-shadow,border-color] duration-300 hover:shadow-lg hover:border-slate-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#2563EB] text-white shrink-0">
                  <Mail className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-display font-bold text-base tracking-tight">Email</p>
                  <p className="text-sm text-slate-500 break-all">{SITE.email}</p>
                </div>
              </a>
              <div className="flex items-center gap-4 border border-slate-200 rounded-2xl p-6 bg-[#F5F8FF]">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white border border-slate-200 text-[#1E2A78] shrink-0">
                  <Clock className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-display font-bold text-base tracking-tight">Business hours</p>
                  <p className="text-sm text-slate-500">{SITE.hours}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <Eyebrow>Send a message</Eyebrow>
            <form onSubmit={submit} className="border border-slate-200 rounded-2xl p-8 md:p-10 bg-white space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <input data-testid="contact-input-name" className={inputCls} placeholder="Your full name" value={form.name} onChange={update("name")} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input data-testid="contact-input-email" type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={update("email")} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-2">Phone *</label>
                  <input data-testid="contact-input-phone" className={inputCls} placeholder="+91 98765 43210" value={form.phone} onChange={update("phone")} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Service needed *</label>
                  <select data-testid="contact-select-service" className={inputCls} value={form.service} onChange={update("service")}>
                    <option value="">Select a service</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.name}>{s.name}</option>
                    ))}
                    <option value="Other">Other / Not sure</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Message *</label>
                <textarea
                  data-testid="contact-input-message"
                  rows={5}
                  className={inputCls}
                  placeholder="Tell us about your topic, academic level, and deadline..."
                  value={form.message}
                  onChange={update("message")}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2.5 rounded-full bg-[#1E2A78] px-8 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-[#2563EB] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={1.5} />}
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
